import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { clearStoredData } from '../utils/localStorage';
import { 
  CHAT_STORAGE_KEY, 
  DEFAULT_OPENROUTER_API_KEY, 
  DEFAULT_MAX_CYCLES,
  OPENROUTER_API_KEY_STORAGE,
  MAX_CYCLES_STORAGE
} from '../utils/config';
import { 
  getSession, 
  createSession, 
  getStoredSessions,
  updateSessionMemoryContext,
  updateSessionProcessingState,
  updateSessionApiSettings,
  ProcessingState,
  ApiSettings
} from '../utils/sessionStorage';
import { 
  processQueryAsync,
} from '../utils/api';
import { generateMessageId } from '../utils/messageUtils';
import {
  Message,
  MemoryContext,
} from '../utils/types';

// Chat context interface
interface ChatContextType {
  loading: boolean;
  currentSessionId: string | null;
  memoryContext: MemoryContext | null;
  apiSettings: ApiSettings;
  processingState: ProcessingState | null;
  setMemoryContext: (context: MemoryContext | null) => void;
  setProcessingState: (state: ProcessingState | null) => void;
  setApiSettings: (settings: ApiSettings) => void;
  addUserMessage: (content: string) => void;
  clearChat: () => void;
  handleRelatedQueryClick: (query: string) => void;
  replaceConversation: (messages: Message[]) => void;
  setCurrentSessionId: (sessionId: string) => void;
  startNewSession: () => void;
  createAndNavigateToNewSession: (initialContent: string) => string;
  processUserMessageAsync: (memoryContext: MemoryContext, sessionId: string, apiSettings: ApiSettings) => void;
}

// Create context with default values
const ChatContext = createContext<ChatContextType>({
  loading: false,
  currentSessionId: null,
  memoryContext: null,
  apiSettings: { apiKey: '', maxCycles: 2 },
  processingState: null,
  setMemoryContext: () => {},
  setProcessingState: () => {},
  setApiSettings: () => {},
  addUserMessage: () => {},
  clearChat: () => {},
  handleRelatedQueryClick: () => {},
  replaceConversation: () => {},
  setCurrentSessionId: () => {},
  startNewSession: () => {},
  createAndNavigateToNewSession: () => '',
  processUserMessageAsync: () => {},
});

// Provider component
export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [processingState, setProcessingState] = useState<ProcessingState | null>(null);
  const [memoryContext, setMemoryContext] = useState<MemoryContext | null>(null);
  const [apiSettings, setApiSettings] = useState<ApiSettings>({
    apiKey: localStorage.getItem(OPENROUTER_API_KEY_STORAGE) || DEFAULT_OPENROUTER_API_KEY,
    maxCycles: Number(localStorage.getItem(MAX_CYCLES_STORAGE)) || DEFAULT_MAX_CYCLES
  });
  
  // Compute loading state based on the last message in conversation
  const loading = useMemo(() => {
    if (!memoryContext || memoryContext.conversation_history.length === 0) {
      return false;
    }
    const lastMessage = memoryContext.conversation_history[memoryContext.conversation_history.length - 1];
    return lastMessage.role === 'user';
  }, [memoryContext]);

  // Function to explicitly save all current session state to localStorage
  const saveCurrentSessionState = useCallback(() => {
    if (!currentSessionId) return;
    
    console.log(`[ChatContext] Explicitly saving all state for session: ${currentSessionId}`);
    
    // Save memory context
    if (memoryContext) {
      updateSessionMemoryContext(currentSessionId, memoryContext);
    }
    
    // Save processing state
    updateSessionProcessingState(currentSessionId, processingState);
    
    // Save API settings
    updateSessionApiSettings(currentSessionId, apiSettings);
  }, [currentSessionId, memoryContext, processingState, apiSettings]);

  // Enhanced API settings setter that persists settings globally
  const updateApiSettings = useCallback((settings: ApiSettings) => {
    // Update global settings in localStorage for new sessions
    localStorage.setItem(OPENROUTER_API_KEY_STORAGE, settings.apiKey);
    localStorage.setItem(MAX_CYCLES_STORAGE, settings.maxCycles.toString());
    
    // Update React state
    setApiSettings(settings);
    
    // If there's an active session, update its settings
    if (currentSessionId) {
      updateSessionApiSettings(currentSessionId, settings);
    }
  }, [currentSessionId]);

  // Process a user message asynchronously - pure function with no side effects
  const processUserMessageAsync = useCallback((memoryContext: MemoryContext, sessionId: string, apiSettings: ApiSettings): void => {
    if (!sessionId) return;
    
    // Important: Capture the session ID
    console.log(`[ChatContext] Starting async processing for session: ${sessionId}`);
    
    // Extract the memory context directly passed as parameter
    const currentMemoryContext = memoryContext;
    
    // Call API without awaiting the response
    processQueryAsync({
      memory_context: currentMemoryContext,
      conversation_id: sessionId,
      title: currentMemoryContext.conversation_history.length > 0 && 
             currentMemoryContext.conversation_history[0].role === 'user' ? 
             currentMemoryContext.conversation_history[0].content : 'New Conversation',
      openrouter_api_key: apiSettings.apiKey || undefined,
      max_cycles: apiSettings.maxCycles || undefined
    }).catch(error => {
      console.error(`[ChatContext] Error starting async processing for session ${sessionId}:`, error);
    });
  }, []);

  // Create a new session only if one doesn't already exist and returns session ID
  const ensureSession = useCallback((content: string, userMessage: Message): string => {
    // If we already have a session ID, use it
    if (currentSessionId) return currentSessionId;
    
    console.log('[ChatContext] Creating new session...');
    
    // Create a new session with full state initialization
    const newSession = createSession(content, userMessage, memoryContext);
    
    // Set the session ID - this will trigger the loading effect
    setCurrentSessionId(newSession.id);
    
    // URL needs to be updated in components that call this function
    return newSession.id;
  }, [currentSessionId, memoryContext]);

  // Add a user message and process it for a response
  const addUserMessage = useCallback((content: string) => {
    if (!content.trim()) return;
    let timestamp = new Date().toISOString();
    const conversationHistory = memoryContext?.conversation_history || [];
    
    const userMessage: Message = {
      id: generateMessageId(conversationHistory, content, 'user', timestamp), // Using Fiat-Shamir style ID generation
      content,
      role: 'user',
      timestamp: timestamp
    };
    
    // Update memory context with the new message
    setMemoryContext(prev => {
      const newMemoryContext: MemoryContext = prev ? {
        ...prev,
        conversation_history: [...prev.conversation_history, userMessage]
      } : {
        conversation_history: [userMessage],
        execution_history: []
      };
      
      // If we need a session, ensure we get one
      if (!currentSessionId) {
        // This will set the currentSessionId state too
        ensureSession(content, userMessage);
      } else {
        // We already have a session, just update it
        updateSessionMemoryContext(currentSessionId, newMemoryContext);
      }
      
      return newMemoryContext;
    });
    
    // Explicitly save all state after user action
    setTimeout(() => saveCurrentSessionState(), 0);
    
    // We don't need to process the message here as that will be handled by the ChatPage component
  }, [currentSessionId, ensureSession, memoryContext, saveCurrentSessionState]);

  // Clear all messages and start a new session
  const clearChat = useCallback(() => {
    setCurrentSessionId(null);
    setMemoryContext(null);
    // Clear legacy storage too
    clearStoredData(CHAT_STORAGE_KEY);
  }, []);

  // Handle related query click
  const handleRelatedQueryClick = useCallback((query: string) => {
    addUserMessage(query);
  }, [addUserMessage]);

  // Replace the entire conversation with new messages
  const replaceConversation = useCallback((newMessages: Message[], newMemoryContext: MemoryContext | null = null) => {
    // Ensure all messages have valid IDs
    const validMessages = newMessages.map(msg => ({
      ...msg,
    }));
    
    // If newMemoryContext is provided, use it, otherwise create one from messages
    const updatedMemoryContext = newMemoryContext ? newMemoryContext : {
      conversation_history: validMessages,
      execution_history: []
    };
    
    // Update memory context
    setMemoryContext(updatedMemoryContext);
    
    // If this is a new conversation, create a new session
    if (validMessages.length > 0 && !currentSessionId) {
      // Use the first user message as the title, if available
      const firstUserMessage = validMessages.find(msg => msg.role === 'user');
      if (firstUserMessage) {
        // No need to check for existing sessions here as this is a deliberately new replacement
        const newSession = createSession(firstUserMessage.content, firstUserMessage, updatedMemoryContext);
        setCurrentSessionId(newSession.id);
      }
    } else if (currentSessionId) {
      // Just update the existing session
      updateSessionMemoryContext(currentSessionId, updatedMemoryContext);
      
      // Explicitly save all state
      setTimeout(() => saveCurrentSessionState(), 0);
    }
  }, [currentSessionId, saveCurrentSessionState]);

  // Start a new session - completely reset all session state
  const startNewSession = useCallback(() => {
    console.log('[ChatContext] Starting new session with clean state');
    
    // Reset all session-specific state to default values
    setMemoryContext(null);
    setProcessingState(null);
    
    // Important: Reset sessionId last to prevent race conditions
    // This will trigger the session loading effect with null ID
    setCurrentSessionId(null);
  }, []);
  
  // Create a new session and navigate to it - centralized implementation
  const createAndNavigateToNewSession = useCallback((initialContent: string): string => {
    console.log('[ChatContext] Creating new session with content:', initialContent);
    
    // First, create the message
    const timestamp = new Date().toISOString();
    const newMessage: Message = {
      id: generateMessageId([], initialContent, 'user', timestamp),
      content: initialContent,
      role: 'user',
      timestamp
    };
    
    // Next, create the session (directly in localStorage)
    const newSession = createSession(initialContent, newMessage);
    console.log(`[ChatContext] Created new session with ID: ${newSession.memoryContext}`);
    
    // IMPORTANT: First set all the state
    setMemoryContext(newSession.memoryContext);
    setProcessingState(newSession.processingState);
    setApiSettings(newSession.apiSettings);
    
    // Then set the session ID - this will trigger loadSessionStateAtomically
    // But since we already set all the state, it won't overwrite anything
    setCurrentSessionId(newSession.id);
    
    console.log(`[ChatContext] Created new session with ID: ${newSession.id}`);
    
    // Start processing immediately for user message
    const sessionId = newSession.id;
    setTimeout(() => {
      if (sessionId && newSession.memoryContext) {
        console.log(`[ChatContext] Created new session with context from memory context: ${newSession.memoryContext?.conversation_history[0].content}`);
        
        // Call the pure function
        processUserMessageAsync(newSession.memoryContext, sessionId, apiSettings);
        
        // Initialize the processing state ourselves
        setProcessingState({
          current_cycle: 0,
          max_cycles: apiSettings.maxCycles || 2,
          progress: 0
        });
      }
    }, 0);
    
    return newSession.id;
  }, [processUserMessageAsync, setMemoryContext, setProcessingState, setApiSettings, apiSettings, setCurrentSessionId]);

  // Atomic session loading function - loads all state at once to prevent race conditions
  const loadSessionStateAtomically = useCallback((sessionId: string | null) => {
    // If no session ID, reset all state
    if (!sessionId) {
      setMemoryContext(null);
      setProcessingState(null);
      return;
    }
    
    // Get the session data
    const session = getSession(sessionId);
    if (!session) {
      console.error(`Session ${sessionId} not found`);
      return;
    }
    
    // Capture current state to detect actual changes
    console.log(`[SessionManager] Loading session ${sessionId} atomically`);
    
    // Update all state at once (React will batch these)
    setMemoryContext(session.memoryContext);
    setProcessingState(session.processingState);
    setApiSettings(session.apiSettings);
  }, []);
  
  // Load complete session state when currentSessionId changes - atomic version
  useEffect(() => {
    loadSessionStateAtomically(currentSessionId);
  }, [currentSessionId, loadSessionStateAtomically]);

  // Initialize with the most recent session on mount if available
  useEffect(() => {
    if (initialized) return;
    
    // Clear legacy storage
    clearStoredData(CHAT_STORAGE_KEY);
    
    // Get existing sessions
    const allSessions = getStoredSessions();
    
    // If there are existing sessions, load the most recent one
    if (allSessions.length > 0) {
      // Sort by updatedAt (newest first)
      const sortedSessions = [...allSessions].sort((a, b) => b.updatedAt - a.updatedAt);
      const mostRecentSession = sortedSessions[0];
      
      // Set the current session ID - this will trigger the session loading effect
      // that loads all the session state from localStorage
      setCurrentSessionId(mostRecentSession.id);
      
      // No need to manually load state here - it will be loaded by the 
      // useEffect that watches currentSessionId
    }
    
    setInitialized(true);
  }, [initialized]);

  return (
    <ChatContext.Provider value={{ 
      loading,
      currentSessionId,
      memoryContext,
      apiSettings,
      processingState,
      setMemoryContext,
      setProcessingState,
      setApiSettings: updateApiSettings, // Use our enhanced version that persists to localStorage
      addUserMessage, 
      clearChat,
      handleRelatedQueryClick,
      replaceConversation,
      setCurrentSessionId,
      startNewSession,
      createAndNavigateToNewSession,
      processUserMessageAsync
    }}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use the chat context
export const useChat = () => useContext(ChatContext);
